'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type PropsWithChildren
} from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '@/components/common/AppModal';
import { Button, Form } from 'react-bootstrap';
import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import { useAuiState } from '@assistant-ui/react';
import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { errorMessage, successMessage } from '@/utils/toast';
import {
  toMcpApiErrorMessage,
  useUpsertMcpMessageFeedbackMutation
} from '@/app/Mcp/api/mcpApi';
import type { McpMessageFeedbackReason } from '@/app/Mcp/types/mcpApiTypes';

type FeedbackRating = 'good' | 'bad';

const FeedbackIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
  >
    <path d="M15.3702 10.3242C15.3702 9.87178 15.3673 9.66262 15.3516 9.53418L15.3311 9.42676C15.2387 9.08236 14.9921 8.8037 14.668 8.66894L14.5255 8.62012C14.4065 8.58823 14.4058 8.58912 14.378 8.58691L13.9171 8.58203L13.7823 8.56836C13.4795 8.50629 13.2522 8.23813 13.252 7.91699C13.252 7.54972 13.5498 7.25195 13.9171 7.25195H15.5538C16.0484 7.25195 16.4596 7.251 16.794 7.27832C17.1361 7.30627 17.4565 7.36647 17.7589 7.52051L17.9288 7.61621C18.3159 7.85359 18.6317 8.19374 18.8389 8.60058L18.8917 8.71484C19.0069 8.98349 19.0567 9.26596 19.0811 9.56543C19.1085 9.89987 19.1075 10.311 19.1075 10.8057V12.4941C19.1053 12.7802 19.095 13.0234 19.0528 13.2422L19.0235 13.3711C18.8093 14.1702 18.211 14.8058 17.4347 15.0703L17.2764 15.1182C16.941 15.208 16.5519 15.2021 16.0352 15.2021C15.9352 15.2022 15.8414 15.2426 15.7725 15.3115L15.712 15.3896L13.6036 19.0801C13.4704 19.3131 13.2098 19.4434 12.9434 19.4102L12.671 19.376C11.1472 19.1855 10.1637 17.665 10.6153 16.1973L10.922 15.2021H10.0001C9.63284 15.2021 9.33508 14.9043 9.33504 14.5371C9.33504 14.1698 9.63281 13.8721 10.0001 13.8721H11.8223C12.0333 13.8721 12.2321 13.972 12.3575 14.1416C12.4828 14.3113 12.5201 14.5308 12.4581 14.7324L11.8868 16.5889C11.6947 17.2134 12.0658 17.8565 12.6759 18.0225L14.5577 14.7295L14.6173 14.6328C14.8037 14.3518 15.0671 14.1366 15.3702 14.0078V10.3242ZM7.07527 0.589843L7.34871 0.624023L7.48934 0.645507C8.8858 0.900505 9.78198 2.28694 9.44149 3.66504L9.4034 3.80273L9.09383 4.80469C9.26639 4.80872 9.42469 4.81491 9.56844 4.82617C9.93535 4.85495 10.2802 4.91643 10.5948 5.08203L10.8272 5.22168C11.3479 5.57355 11.7179 6.11326 11.8555 6.7334L11.88 6.86328C11.9248 7.16766 11.8933 7.4745 11.8331 7.79102C11.799 7.97017 11.7521 8.16961 11.6954 8.39062L11.4991 9.12207L11.2247 10.1299C11.0567 10.746 10.9317 11.2371 10.6895 11.627L10.5782 11.7881C10.3471 12.0873 10.051 12.329 9.71297 12.4951L9.56551 12.5615C9.0832 12.7605 8.52572 12.748 7.79598 12.748H4.46492C3.97039 12.748 3.55908 12.749 3.22469 12.7217C2.92525 12.6972 2.64273 12.6475 2.3741 12.5322L2.25985 12.4795C1.85303 12.2722 1.51283 11.9564 1.27547 11.5693L1.18074 11.3994C1.02663 11.0969 0.965541 10.7768 0.93758 10.4346C0.910255 10.1001 0.911213 9.68897 0.911213 9.19433V7.87012C0.911211 7.35335 0.905318 6.96434 0.995197 6.62891L1.04305 6.47168C1.30751 5.69506 1.9429 5.09604 2.74227 4.88184L2.87117 4.85254C3.17731 4.79349 3.53146 4.79785 3.98348 4.79785C4.11701 4.79785 4.24047 4.72629 4.30672 4.61035L6.41512 0.919921L6.47078 0.83789C6.61342 0.657078 6.84208 0.560753 7.07527 0.589843ZM16.7003 13.8623C16.811 13.8565 16.8768 13.848 16.9327 13.833L17.0763 13.7842C17.4 13.6492 17.6471 13.3706 17.7393 13.0264L17.7589 12.9189C17.7745 12.7905 17.7774 12.5815 17.7774 12.1299V10.8057C17.7774 10.2891 17.7778 9.94154 17.7559 9.67383C17.74 9.47878 17.7144 9.35924 17.6847 9.27637L17.6534 9.2041C17.5713 9.04316 17.4525 8.90503 17.3077 8.7998L17.1554 8.70605C17.0716 8.66338 16.9456 8.62573 16.6856 8.60449C16.6064 8.59802 16.5203 8.59311 16.4249 8.58984C16.505 8.74522 16.57 8.91021 16.6163 9.08301L16.6456 9.21094C16.6879 9.42971 16.6981 9.67298 16.7003 9.95898V13.8623ZM4.64852 9.67578C4.64852 10.2783 4.65443 10.4495 4.68758 10.5732L4.73641 10.7158C4.87123 11.04 5.14969 11.2876 5.49422 11.3799L5.60164 11.3994C5.73008 11.415 5.93904 11.418 6.3907 11.418H7.79598C8.64567 11.418 8.87848 11.4059 9.0577 11.332L9.19149 11.2666C9.32053 11.1924 9.43391 11.0932 9.52547 10.9746L9.56844 10.9131C9.6649 10.7567 9.74567 10.4973 9.94149 9.7793L10.2159 8.77148L10.4073 8.05957C10.4589 7.85882 10.4984 7.6904 10.5264 7.54297C10.568 7.3245 10.5755 7.19026 10.5684 7.09961L10.5577 7.02148C10.4941 6.73503 10.3227 6.48568 10.0821 6.32324L9.97469 6.25879C9.89449 6.2167 9.75934 6.17549 9.46395 6.15234C9.16443 6.12888 8.77153 6.12793 8.19637 6.12793C7.98563 6.12785 7.78753 6.0278 7.66219 5.8584C7.53687 5.68873 7.49869 5.4692 7.56063 5.26758L8.13192 3.41113L8.16317 3.28418C8.27278 2.70117 7.91206 2.13103 7.34285 1.97656L5.46102 5.27051C5.27333 5.59878 4.98538 5.84809 4.64852 5.99121V9.67578ZM2.24129 9.19433C2.24129 9.71091 2.24188 10.0585 2.26375 10.3262C2.28499 10.5861 2.32265 10.7122 2.36531 10.7959L2.45906 10.9482C2.56437 11.0932 2.70323 11.2119 2.86434 11.2939L2.9366 11.3252C3.0194 11.3548 3.13872 11.3796 3.33309 11.3955C3.41216 11.402 3.49855 11.4059 3.59383 11.4092C3.53863 11.3021 3.4897 11.1909 3.45027 11.0752L3.40242 10.917C3.31261 10.5816 3.31844 10.1925 3.31844 9.67578V6.13672C3.26922 6.13929 3.2289 6.1423 3.19442 6.14648L3.08699 6.16699C2.7426 6.25927 2.46406 6.50612 2.32918 6.83008L2.28035 6.97363C2.24726 7.09732 2.24129 7.26819 2.24129 7.87012V9.19433Z" />
  </svg>
);

const FEEDBACK_REASONS = [
  { id: 'incorrect', label: 'نادرست یا ناقص' },
  { id: 'not_asked', label: 'چیزی که خواستم نبود' },
  { id: 'slow_buggy', label: 'کند یا باگ‌دار' },
  { id: 'style_tone', label: 'سبک یا لحن' },
  { id: 'safety', label: 'نگرانی ایمنی یا قانونی' },
  { id: 'other', label: 'سایر' }
] as const;

type FeedbackReasonId = (typeof FEEDBACK_REASONS)[number]['id'];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function resolveServerMessageId(message: {
  id: string;
  metadata?: { custom?: Record<string, unknown> };
}): string | null {
  const customId = message.metadata?.custom?.serverMessageId;
  if (typeof customId === 'string' && UUID_RE.test(customId)) {
    return customId;
  }
  if (UUID_RE.test(message.id)) {
    return message.id;
  }
  return null;
}

function resolveStoredFeedbackRating(message: {
  metadata?: { custom?: Record<string, unknown> };
}): FeedbackRating | null {
  const value = message.metadata?.custom?.feedbackRating;
  if (value === 'good' || value === 'bad') return value;
  if (value === 'positive') return 'good';
  if (value === 'negative') return 'bad';
  return null;
}

type FeedbackContextValue = {
  rating: FeedbackRating | null;
  menuOpen: boolean;
  openMenu: (anchor: DOMRect) => void;
  closeMenu: () => void;
  markGood: () => void;
  markBad: () => void;
};

const MessageFeedbackContext = createContext<FeedbackContextValue | null>(
  null
);

type ShareFeedbackModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string | null;
};

const ShareFeedbackModal: FC<ShareFeedbackModalProps> = ({
  open,
  onOpenChange,
  messageId
}) => {
  const [reasons, setReasons] = useState<FeedbackReasonId[]>([]);
  const [details, setDetails] = useState('');
  const [upsertFeedback, { isLoading: submitting }] =
    useUpsertMcpMessageFeedbackMutation();

  useEffect(() => {
    if (!open) return;
    setReasons([]);
    setDetails('');
  }, [open, messageId]);

  const canSubmit = reasons.length > 0 || details.trim().length > 0;

  const toggleReason = (id: FeedbackReasonId) => {
    setReasons(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    if (!messageId) {
      errorMessage('شناسه پیام برای ثبت بازخورد در دسترس نیست');
      return;
    }
    try {
      await upsertFeedback({
        messageId,
        rating: 'negative',
        reasons: reasons as McpMessageFeedbackReason[],
        details: details.trim() || undefined
      }).unwrap();
      onOpenChange(false);
      successMessage('بازخورد شما ثبت شد');
    } catch (error) {
      errorMessage(toMcpApiErrorMessage(error, 'ثبت بازخورد ناموفق بود'));
    }
  };

  return (
    <Modal
      show={open}
      onHide={() => onOpenChange(false)}
      centered
      dir="rtl"
      contentClassName="border-0 shadow rounded-3"
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <Modal.Title className="fs-0 mb-0">ارسال بازخورد</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        <div className="d-flex flex-wrap gap-2 mb-3">
          {FEEDBACK_REASONS.map(reason => {
            const selected = reasons.includes(reason.id);
            return (
              <Button
                key={reason.id}
                type="button"
                size="sm"
                variant={selected ? 'dark' : 'falcon-default'}
                className="rounded-pill"
                aria-pressed={selected}
                onClick={() => toggleReason(reason.id)}
              >
                {reason.label}
              </Button>
            );
          })}
        </div>

        <Form.Control
          as="textarea"
          rows={4}
          value={details}
          onChange={e => setDetails(e.target.value)}
          placeholder="جزئیات را بنویسید (اختیاری)"
          className="rounded-3"
        />

        <div className="bg-200 text-600 rounded-3 px-3 py-2 mt-3 fs--1">
          مکالمه شما همراه با بازخورد ارسال می‌شود تا به بهبود دستیار کمک کند.
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top py-3 px-4">
        <Button
          type="button"
          variant="primary"
          className="rounded-pill px-4"
          disabled={!canSubmit || submitting}
          onClick={() => {
            void handleSubmit();
          }}
        >
          {submitting ? 'در حال ارسال…' : 'ارسال'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

type FeedbackMenuProps = {
  open: boolean;
  anchor: DOMRect | null;
  rating: FeedbackRating | null;
  onClose: () => void;
  onGood: () => void;
  onBad: () => void;
};

const FeedbackMenu: FC<FeedbackMenuProps> = ({
  open,
  anchor,
  rating,
  onClose,
  onGood,
  onBad
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open || !anchor) return null;

  const style: CSSProperties = {
    position: 'fixed',
    bottom: window.innerHeight - anchor.top + 6,
    left: Math.min(Math.max(8, anchor.left - 40), window.innerWidth - 200),
    zIndex: 1070
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="بازخورد پاسخ"
      dir="rtl"
      style={style}
      className="mcp-feedback-menu"
    >
      <button
        type="button"
        role="menuitem"
        className={cn(
          'mcp-feedback-menu-item',
          rating === 'good' && 'is-active'
        )}
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onGood();
        }}
      >
        <ThumbsUpIcon
          className={cn('size-3 shrink-0', rating === 'good' && 'fill-current')}
        />
        پاسخ خوب
      </button>
      <button
        type="button"
        role="menuitem"
        className={cn(
          'mcp-feedback-menu-item',
          rating === 'bad' && 'is-active'
        )}
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onBad();
        }}
      >
        <ThumbsDownIcon
          className={cn('size-3 shrink-0', rating === 'bad' && 'fill-current')}
        />
        پاسخ بد
      </button>
    </div>,
    document.body
  );
};

/**
 * Keeps feedback menu/modal state outside the action bar so UI survives
 * hover/focus changes after opening feedback.
 */
export const MessageFeedbackHost: FC<PropsWithChildren> = ({ children }) => {
  const message = useAuiState(s => s.message);
  const serverMessageId = resolveServerMessageId(message);
  const [rating, setRating] = useState<FeedbackRating | null>(() =>
    resolveStoredFeedbackRating(message)
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const openModalTimerRef = useRef<number | null>(null);
  const savingRef = useRef(false);
  const [upsertFeedback] = useUpsertMcpMessageFeedbackMutation();

  useEffect(() => {
    const stored = resolveStoredFeedbackRating(message);
    if (stored) setRating(stored);
  }, [message.id, message.metadata?.custom?.feedbackRating]);

  useEffect(() => {
    return () => {
      if (openModalTimerRef.current != null) {
        window.clearTimeout(openModalTimerRef.current);
      }
    };
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuAnchor(null);
  }, []);

  const openMenu = useCallback((anchor: DOMRect) => {
    setMenuAnchor(anchor);
    setMenuOpen(true);
  }, []);

  const requireMessageId = useCallback((): string | null => {
    if (serverMessageId) return serverMessageId;
    errorMessage(
      'این پیام هنوز در سرور ذخیره نشده. بعد از اتمام پاسخ دوباره تلاش کنید.'
    );
    return null;
  }, [serverMessageId]);

  const markGood = useCallback(() => {
    const messageId = requireMessageId();
    setMenuOpen(false);
    setMenuAnchor(null);
    if (!messageId || savingRef.current) return;

    savingRef.current = true;
    setRating('good');
    void upsertFeedback({
      messageId,
      rating: 'positive'
    })
      .unwrap()
      .then(() => {
        successMessage('بازخورد مثبت ثبت شد');
      })
      .catch(error => {
        setRating(null);
        errorMessage(toMcpApiErrorMessage(error, 'ثبت بازخورد ناموفق بود'));
      })
      .finally(() => {
        savingRef.current = false;
      });
  }, [requireMessageId, upsertFeedback]);

  const markBad = useCallback(() => {
    const messageId = requireMessageId();
    setMenuOpen(false);
    setMenuAnchor(null);
    if (!messageId || savingRef.current) return;

    savingRef.current = true;
    setRating('bad');
    void upsertFeedback({
      messageId,
      rating: 'negative'
    })
      .unwrap()
      .then(() => {
        if (openModalTimerRef.current != null) {
          window.clearTimeout(openModalTimerRef.current);
        }
        openModalTimerRef.current = window.setTimeout(() => {
          setModalOpen(true);
          openModalTimerRef.current = null;
        }, 10);
      })
      .catch(error => {
        setRating(null);
        errorMessage(toMcpApiErrorMessage(error, 'ثبت بازخورد ناموفق بود'));
      })
      .finally(() => {
        savingRef.current = false;
      });
  }, [requireMessageId, upsertFeedback]);

  const value = useMemo(
    () => ({ rating, menuOpen, openMenu, closeMenu, markGood, markBad }),
    [rating, menuOpen, openMenu, closeMenu, markGood, markBad]
  );

  return (
    <MessageFeedbackContext.Provider value={value}>
      {children}
      <FeedbackMenu
        open={menuOpen}
        anchor={menuAnchor}
        rating={rating}
        onClose={closeMenu}
        onGood={markGood}
        onBad={markBad}
      />
      <ShareFeedbackModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        messageId={serverMessageId}
      />
    </MessageFeedbackContext.Provider>
  );
};

/**
 * Single feedback icon — click opens Good/Bad response menu.
 * Must render under `MessageFeedbackHost`.
 */
export const MessageFeedbackButtons: FC = () => {
  const ctx = useContext(MessageFeedbackContext);
  const buttonRef = useRef<HTMLButtonElement>(null);
  if (!ctx) return null;

  const { rating, menuOpen, openMenu, closeMenu } = ctx;

  return (
    <div className="relative inline-flex items-center">
      <TooltipIconButton
        ref={buttonRef}
        tooltip="بازخورد"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className={cn(
          (menuOpen || rating) && 'text-foreground',
          menuOpen && 'bg-accent'
        )}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (menuOpen) {
            closeMenu();
            return;
          }
          const el = buttonRef.current ?? (e.currentTarget as HTMLButtonElement);
          openMenu(el.getBoundingClientRect());
        }}
      >
        <FeedbackIcon className="mcp-feedback-icon size-4" />
      </TooltipIconButton>
    </div>
  );
};
